import { MortgageData } from '@/types/calculator';
import { calculateMortgageData } from '@/utils/calculator/mortgageCalculator';
import { formatInterestRate } from '@/components/calculator/utils/formatters';

export interface PITIBreakdown {
  principal: number;
  interest: number;
  taxes: number;
  insurance: number;
  pmi: number;
  hoa: number;
  total: number;
}

export interface PITIComparison {
  current: {
    breakdown: PITIBreakdown;
    scenario: string;
    rate: number;
    loanAmount: number;
  };
  improved: {
    breakdown: PITIBreakdown;
    scenario: string;
    rate: number;
    loanAmount: number;
  };
  savings: {
    monthly: number;
    annual: number;
    lifetime: number;
    percentImprovement: number;
  };
}

export const calculatePITIBreakdown = (mortgageData: MortgageData): PITIBreakdown => {
  const calculated = calculateMortgageData(mortgageData);
  
  return {
    principal: calculated.monthlyPayment.principal,
    interest: calculated.monthlyPayment.interest,
    taxes: calculated.monthlyPayment.taxes,
    insurance: calculated.monthlyPayment.insurance,
    pmi: calculated.monthlyPayment.pmi,
    hoa: calculated.monthlyPayment.hoa,
    total: calculated.monthlyPayment.total
  };
};

export const calculatePITIComparison = (
  currentMortgageData: MortgageData,
  improvedMortgageData: MortgageData,
  currentScenario: string,
  improvedScenario: string,
  loanTermYears: number = 30
): PITIComparison => {
  const currentBreakdown = calculatePITIBreakdown(currentMortgageData);
  const improvedBreakdown = calculatePITIBreakdown(improvedMortgageData);
  
  const monthlySavings = currentBreakdown.total - improvedBreakdown.total;
  const annualSavings = monthlySavings * 12;
  const lifetimeSavings = monthlySavings * loanTermYears * 12;
  const percentImprovement = currentBreakdown.total > 0 ? 
    (monthlySavings / currentBreakdown.total) * 100 : 0;

  return {
    current: {
      breakdown: currentBreakdown,
      scenario: currentScenario,
      rate: currentMortgageData.interestRate,
      loanAmount: currentMortgageData.homePrice - currentMortgageData.downPayment
    },
    improved: {
      breakdown: improvedBreakdown,
      scenario: improvedScenario,
      rate: improvedMortgageData.interestRate,
      loanAmount: improvedMortgageData.homePrice - improvedMortgageData.downPayment
    },
    savings: {
      monthly: monthlySavings,
      annual: annualSavings,
      lifetime: lifetimeSavings,
      percentImprovement
    }
  };
};

export const createCreditScoreImprovementComparison = (
  mortgageData: MortgageData,
  currentCreditScore: number,
  improvedCreditScore: number,
  loanTermYears: number = 30
): PITIComparison => {
  // Rate improvement based on credit score increases
  let rateImprovement = 0;
  
  if (currentCreditScore < 680 && improvedCreditScore >= 680) {
    rateImprovement = 0.75; // Significant improvement from poor to fair
  } else if (currentCreditScore < 720 && improvedCreditScore >= 720) {
    rateImprovement = 0.5; // Good improvement from fair to good
  } else if (currentCreditScore < 760 && improvedCreditScore >= 760) {
    rateImprovement = 0.25; // Modest improvement from good to excellent
  }

  const improvedMortgageData = {
    ...mortgageData,
    interestRate: Math.max(3.0, mortgageData.interestRate - rateImprovement)
  };

  return calculatePITIComparison(
    mortgageData,
    improvedMortgageData,
    `Current Credit Score (${currentCreditScore})`,
    `Improved Credit Score (${improvedCreditScore})`,
    loanTermYears
  );
};

export const createRefinancingComparison = (
  mortgageData: MortgageData,
  newRate: number,
  closingCosts: number = 0,
  loanTermYears: number = 30
): PITIComparison & { breakEvenMonths: number } => {
  const improvedMortgageData = {
    ...mortgageData,
    interestRate: newRate
  };

  const comparison = calculatePITIComparison(
    mortgageData,
    improvedMortgageData,
    `Current Rate (${formatInterestRate(mortgageData.interestRate)})`,
    `Refinanced Rate (${formatInterestRate(newRate)})`,
    loanTermYears
  );

  const breakEvenMonths = comparison.savings.monthly > 0 ? 
    Math.ceil(closingCosts / comparison.savings.monthly) : 999;

  return {
    ...comparison,
    breakEvenMonths
  };
};

export const createExtraPaymentComparison = (
  mortgageData: MortgageData,
  extraPaymentAmount: number,
  loanTermYears: number = 30
): {
  comparison: PITIComparison;
  yearsReduced: number;
  interestSavings: number;
} => {
  // Calculate years and interest saved with extra payment
  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const monthlyRate = mortgageData.interestRate / 100 / 12;
  const basePayment = calculatePITIBreakdown(mortgageData);
  
  // Calculate payoff time with extra payment
  let balance = loanAmount;
  let months = 0;
  let totalInterestPaid = 0;
  
  while (balance > 0 && months < loanTermYears * 12) {
    const interestPayment = balance * monthlyRate;
    const principalPayment = basePayment.principal + extraPaymentAmount;
    
    totalInterestPaid += interestPayment;
    balance = Math.max(0, balance - principalPayment);
    months++;
  }
  
  const yearsReduced = Math.max(0, loanTermYears - (months / 12));
  const standardTotalInterest = (basePayment.total * loanTermYears * 12) - loanAmount;
  const interestSavings = Math.max(0, standardTotalInterest - totalInterestPaid);
  
  // Create comparison showing extra payment impact
  const improvedBreakdown = {
    ...basePayment,
    principal: basePayment.principal + extraPaymentAmount,
    total: basePayment.total + extraPaymentAmount
  };
  
  const comparison: PITIComparison = {
    current: {
      breakdown: basePayment,
      scenario: "Standard Payment",
      rate: mortgageData.interestRate,
      loanAmount: loanAmount
    },
    improved: {
      breakdown: improvedBreakdown,
      scenario: `With $${extraPaymentAmount} Extra`,
      rate: mortgageData.interestRate,
      loanAmount: loanAmount
    },
    savings: {
      monthly: -extraPaymentAmount, // Negative because you pay more monthly
      annual: -extraPaymentAmount * 12,
      lifetime: interestSavings, // But save on total interest
      percentImprovement: (interestSavings / standardTotalInterest) * 100
    }
  };
  
  return {
    comparison,
    yearsReduced,
    interestSavings
  };
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercentage = (percentage: number): string => {
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
};